import { useEffect, useMemo, useState, Fragment } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { Scale } from "lucide-react";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";

export default function TrialBalance() {
  const [data, setData] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    api.get("/branches").then(r => setBranches(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  async function load() {
    const qs = new URLSearchParams();
    if (fromDate) qs.set("from_date", fromDate);
    if (toDate) qs.set("to_date", toDate);
    if (branchId) qs.set("branch_id", branchId);
    const { data } = await api.get(`/ledger/trial-balance?${qs}`);
    setData(data);
  }
  useEffect(() => { load(); }, [fromDate, toDate, branchId]);

  // Sort once so accounts of the same type sit together. Group dividers are
  // rendered inline by tracking `prevType` during the .map of the paged slice.
  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    return [...data.rows].sort((a, b) => {
      if (a.account_type !== b.account_type) return a.account_type.localeCompare(b.account_type);
      return (a.account_name || "").localeCompare(b.account_name || "");
    });
  }, [data]);

  const { paged, page, pageSize, total, setPage, setPageSize } =
    useClientPagination(sortedRows, { initialPageSize: 50 });

  if (!data) return <div className="text-slate-500">Loading trial balance…</div>;

  const balanced = Math.abs(data.total_debit - data.total_credit) < 0.01;

  return (
    <div>
      <PageHeader
        eyebrow="Accounting"
        title="Trial Balance"
        subtitle="Sum of all ledger postings. Debits must equal credits — confirming books are in balance."
        actions={
          <div className="flex items-center gap-2">
            <select
              value={branchId}
              onChange={e => setBranchId(e.target.value)}
              className="border border-slate-300 px-3 py-2 text-xs bg-white"
              data-testid="tb-branch-filter"
            >
              <option value="">All Branches</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.branch_name || b.branch_code || b.id}</option>
              ))}
              <option value="__none__">⚠ Unmapped (no branch)</option>
            </select>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="border border-slate-300 px-3 py-2 text-xs" />
            <span className="text-xs text-slate-400">to</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="border border-slate-300 px-3 py-2 text-xs" />
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Total Debit</div>
          <div className="font-display text-2xl font-black tabular mt-2 text-slate-900">{formatINR(data.total_debit)}</div>
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Total Credit</div>
          <div className="font-display text-2xl font-black tabular mt-2 text-slate-900">{formatINR(data.total_credit)}</div>
        </Card>
        <Card className={`p-5 ${balanced ? "border-emerald-300" : "border-red-300"}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2"><Scale size={11} /> Status</div>
          <div className={`font-display text-2xl font-black mt-2 ${balanced ? "text-emerald-700" : "text-red-700"}`}>
            {balanced ? "✓ BALANCED" : "× MISMATCH"}
          </div>
          {!balanced && <div className="text-xs text-red-600 mt-1">Diff: {formatINR(Math.abs(data.total_debit - data.total_credit))}</div>}
        </Card>
      </div>

      {sortedRows.length === 0 ? <EmptyState title="No ledger entries in selected range" /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="trial-balance-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Account Type", "Account", "Debit", "Credit", "Balance"].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {(() => {
                  // Compute the "previous type" relative to the page-start row, so the
                  // very first row of each page still gets its group header.
                  const startIdx = (page - 1) * pageSize;
                  let prevType = startIdx > 0 ? sortedRows[startIdx - 1]?.account_type : null;
                  return paged.map((r, i) => {
                    const showHeader = r.account_type !== prevType;
                    prevType = r.account_type;
                    const rowKey = `${r.account_type}-${r.account_id ?? i}-${startIdx + i}`;
                    return (
                      <Fragment key={rowKey}>
                        {showHeader && (
                          <tr className="bg-slate-100 border-b border-slate-200">
                            <td colSpan={5} className="py-2 px-4 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">{r.account_type.replace(/_/g, " ")}</td>
                          </tr>
                        )}
                        <tr className="border-b border-slate-100 hover:bg-slate-50/60">
                          <td className="py-2.5 px-4 text-xs uppercase text-slate-500">{r.account_type.replace(/_/g, " ")}</td>
                          <td className="py-2.5 px-4 text-sm text-slate-800">{r.account_name}</td>
                          <td className="py-2.5 px-4 text-sm tabular">{r.debit ? formatINR(r.debit) : ""}</td>
                          <td className="py-2.5 px-4 text-sm tabular">{r.credit ? formatINR(r.credit) : ""}</td>
                          <td className={`py-2.5 px-4 text-sm tabular font-bold ${r.balance >= 0 ? "text-slate-800" : "text-[#F97316]"}`}>
                            {formatINR(Math.abs(r.balance))} <span className="text-[10px] font-normal text-slate-500">{r.balance_type}</span>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  });
                })()}
              </tbody>
              <tfoot className="bg-slate-900 text-white font-bold text-sm">
                <tr>
                  <td colSpan={2} className="py-3 px-4 uppercase tracking-wider text-[10px]">Grand Total</td>
                  <td className="py-3 px-4 tabular">{formatINR(data.total_debit)}</td>
                  <td className="py-3 px-4 tabular">{formatINR(data.total_credit)}</td>
                  <td className="py-3 px-4 tabular">{balanced ? "0" : formatINR(Math.abs(data.total_debit - data.total_credit))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <PaginationControls page={page} pageSize={pageSize} total={total}
            onPage={setPage} onPageSize={setPageSize}
            pageSizeOptions={[25, 50, 100, 200]} />
        </Card>
      )}
    </div>
  );
}
